import {Component, Element, Event, EventEmitter, h, Host, Method, Prop, State, Watch} from "@stencil/core"
import {validateProp} from "../../../helpers/Validate/props"
import {
	ButtonClass,
	ButtonColors,
	ButtonColorType,
	ButtonSizesType,
	ButtonTargetType,
	ButtonType,
	ButtonTypesType,
	NativeType,
	NativeTypesType,
} from "./constants"
import {govHost, toBoolAttrIfDefined} from "../../../helpers/Dom/template"
import {GovHost} from "../../../helpers/Dom/template.types"
import {toBool} from "../../../utils/bool.utils"
import {Sizes} from "../../../core/constants/sizes"
import {validateAccessibleLabel} from "../../../helpers/Validate/wcag"
import {canValidateWcagOnRender} from "../../../helpers/Dom/win"
import {delay} from "../../../utils/utils"
import {ButtonEvent} from "./gov-button.types"
import {inheritAriaAttributes} from "../../../helpers/Accessibility/accessibility";
import {ariaAttributes} from "../../../helpers/Accessibility/attributes";
import {AttributeObserver, Attributes} from "../../../helpers/Dom/attribute";

enum Tags {
	A = "a",
	BUTTON = "button",
}

@Component({
	tag: "gov-button",
})
export class GovButton {
	private h: GovHost
	private triggerRef?: HTMLButtonElement | HTMLLinkElement
	private attributeObserver: AttributeObserver
	private hydrated = false

	constructor() {
		this.h = govHost(this.host)
	}

	@Element() host: HTMLGovButtonElement

	/**
	 * Style variation of the button
	 */
	@Prop({reflect: true}) readonly color: ButtonColorType
	/**
	 * Button’s size
	 */
	@Prop({reflect: true}) readonly size?: ButtonSizesType = "m"
	/**
	 * Button's type
	 */
	@Prop({reflect: true}) readonly type: ButtonTypesType
	/**
	 * Expands the button to fill 100% of the container width
	 */
	@Prop({reflect: true}) readonly expanded: boolean = false
	/**
	 * Expands the button to fill 100% of the container width only on mobile
	 */
	@Prop({reflect: true, attribute: "expanded-mobile"}) readonly expandedMobile: boolean = false
	/**
	 * The name of the button, which gets paired with the button's value when submitted as part of a form. Corresponds with the native HTML name attribute
	 */
	@Prop() readonly name: string
	/**
	 * Makes the button component disabled.
	 * This prevents users from being able to interact with the button, and conveys its inactive state to assistive technologies
	 */
	@Prop({reflect: true}) readonly disabled: boolean = false
	/**
	 * Same as original parameter
	 * https://developer.mozilla.org/en-US/docs/Web/HTML/Element/button#attr-type
	 */
	@Prop() readonly nativeType?: NativeTypesType = "button"
	/**
	 * Same as original parameter
	 * @link https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a#attr-href
	 */
	@Prop() readonly href: string
	/**
	 * Same as original parameter
	 * @link https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a#attr-target
	 */
	@Prop() readonly target?: ButtonTargetType
	/**
	 * Same as original parameter
	 * @link https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a#attr-download
	 */
	@Prop() readonly download: boolean
	/**
	 * Same as original parameter
	 * @link https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a#attr-download
	 */
	@Prop() readonly tab: number = 0
	/**
	 * Same as original parameter
	 * @link https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a#attr-hreflang
	 */
	@Prop() readonly hreflang: string
	/**
	 * Same as original parameter
	 * @link https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a#attr-referrerpolicy
	 */
	@Prop() readonly referrerpolicy: string
	/**
	 * Same as original parameter
	 * @link https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a#attr-rel
	 */
	@Prop() readonly rel: string
	/**
	 * Indicates whether an asynchronous process is running
	 */
	@Prop() readonly loading: string
	/**
	 * Same as original parameter
	 * @link https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes/focusable
	 */
	@Prop() readonly focusable: boolean = true
	/**
	 * Custom button identifier
	 */
	@Prop({attribute: "identifier"}) readonly identifier: string
	/**
	 * Emitted when the button click
	 */
	@Event({eventName: "gov-click"}) govClick: EventEmitter<ButtonEvent>
	/**
	 * Emitted when the button has focus
	 */
	@Event({eventName: "gov-focus"}) govFocus: EventEmitter<ButtonEvent>
	/**
	 * Emitted when the button loses focus
	 */
	@Event({eventName: "gov-blur"}) govBlur: EventEmitter<ButtonEvent>

	@Watch("color")
	validateColor(newValue: string): void {
		validateProp(ButtonColors, newValue, ButtonClass.root)
	}

	@Watch("type")
	validateType(newValue: string): void {
		validateProp(ButtonType, newValue, ButtonClass.root)
	}

	@Watch("size")
	validateSize(newValue: string): void {
		validateProp(Sizes, newValue, ButtonClass.root)
	}

	@Watch("nativeType")
	validateNativeType(newValue: string): void {
		validateProp(NativeType, newValue, ButtonClass.root)
	}

	@State() inheritedAttributes: Attributes = {}

	componentDidLoad() {
		this.attributeObserver = new AttributeObserver(this.host, (attributeName, newValue) => {
			this.onAriaAttributeChange(attributeName, newValue);
		});

		this.attributeObserver.startObserving(ariaAttributes);
	}

	onAriaAttributeChange(attributeName: string, newValue: string) {
		this.host.removeAttribute(attributeName)
		this.inheritedAttributes = {...this.inheritedAttributes, [attributeName]: newValue};
	}

	disconnectedCallback() {
		this.attributeObserver?.stopObserving();
	}

	componentWillLoad(): void {
		this.validateColor(this.color)
		this.validateType(this.type)
		this.validateSize(this.size)
		this.validateNativeType(this.nativeType)
		this.inheritedAttributes = inheritAriaAttributes(this.host);
	}

	async componentDidRender() {
		if (canValidateWcagOnRender()) {
			await delay(500)
			await this.validateAccessibility()
		}
	}

	connectedCallback() {
		this.hydrated = true
	}

	render() {
		const Tag = this.href ? Tags.A : Tags.BUTTON
		const nativeType = Tag === Tags.A ? undefined : this.nativeType

		return (
			<Host size={this.size}
				  hydrated={this.hydrated}
				  color={this.color}
				  class={this.h.classes(ButtonClass.root)}
				  type={this.type}>
				<Tag
					class="element"
					ref={el => (this.triggerRef = el as HTMLButtonElement | HTMLLinkElement)}
					onClick={this.onClickHandler.bind(this)}
					onFocus={this.onFocusHandler.bind(this)}
					onBlur={this.onBlurHandler.bind(this)}
					disabled={this.disabled}
					id={this.identifier}
					href={this.href}
					target={this.target}
					download={this.download}
					hreflang={this.hreflang}
					rel={this.rel}
					name={this.name}
					type={nativeType}
					tabindex={this.disabled ? "-1" : this.tab}
					{...this.inheritedAttributes}
					aria-disabled={toBoolAttrIfDefined(this.disabled)}
				>
					{toBool(this.loading) && !this.h.hasSlot("icon-end") &&
						<gov-icon name="loader" class="gov-spin-animation" type="components"></gov-icon>}
					{this.h.hasSlot("icon-start") && <slot name="icon-start"></slot>}
					<slot/>
					{this.h.hasSlot("icon-end") && <slot name="icon-end"></slot>}
					{toBool(this.loading) && this.h.hasSlot("icon-end") &&
						<gov-icon name="loader" class="gov-spin-animation" type="components"></gov-icon>}
				</Tag>
			</Host>
		)
	}

	private onClickHandler(e: PointerEvent): void {
		e.stopPropagation()
		if (this.disabled === false) {
			this.govClick.emit({
				component: ButtonClass.root,
				originalEvent: e,
				ref: this.host,
			})
		}
	}

	private onFocusHandler(e: FocusEvent) {
		e.stopPropagation()
		this.govFocus.emit({
			component: ButtonClass.root,
			originalEvent: e,
			ref: this.host,
		})
	}

	private onBlurHandler(e: FocusEvent) {
		e.stopPropagation()
		this.govBlur.emit({
			component: ButtonClass.root,
			originalEvent: e,
			ref: this.host,
		})
	}

	/**
	 * Focus button element
	 */
	@Method()
	async setFocus(): Promise<void> {
		this.triggerRef.focus()
	}

	/**
	 * Returns a clickable element instance
	 */
	@Method()
	async getRef() {
		return this.triggerRef
	}

	/**
	 * Validate the Accessibility attributes of the component
	 */
	@Method()
	async validateAccessibility(): Promise<void> {
		if (this.focusable) {
			validateAccessibleLabel(this.triggerRef.getAttribute('aria-label'), this.triggerRef.getAttribute('aria-labelledby'), ButtonClass.root, this.host)
		}
	}
}
